/**
 * Web Destination Provider — File System Access API.
 *
 * Uses showDirectoryPicker() so the user explicitly chooses
 * the destination BEFORE the download starts. No save-dialog-after-complete.
 *
 * Falls back to anchor-element download for browsers without FSAPI
 * (Firefox, older Safari). The fallback doesn't need a handle since
 * files are streamed via getBlob() to anchor download.
 */

import type { PlatformDestinationProvider } from './destination';
import type { TorrentDestination } from '../networking/engines/torrent/torrent-engine';

export class WebDestinationProvider implements PlatformDestinationProvider {
  async chooseDestination(
    _suggestedFileName: string
  ): Promise<TorrentDestination> {
    if (typeof window !== 'undefined' && 'showDirectoryPicker' in window) {
      try {
        const handle = await (window as any).showDirectoryPicker({
          mode: 'readwrite',
          startIn: 'downloads',
        });
        return { kind: 'browser-directory', handle };
      } catch (err: any) {
        if (err?.name === 'AbortError') {
          throw err;
        }
        return { kind: 'browser-stream-fallback' };
      }
    }

    // Browser doesn't support FileSystem Access API (Firefox, Safari)
    // Return graceful fallback descriptor
    return { kind: 'browser-stream-fallback' };
  }

  async defaultDestination(
    _suggestedFileName: string
  ): Promise<TorrentDestination> {
    if (typeof window !== 'undefined' && 'showDirectoryPicker' in window) {
      // In Chromium with FSAPI, user must explicitly choose
      throw new Error(
        'Browser requires user to explicitly choose a download destination.'
      );
    }
    // On browsers without directory picker, stream fallback is used
    return { kind: 'browser-stream-fallback' };
  }
}
